import EvaluationFlow from "../../../../flow/EvaluationFlow"
import { InAppMessageEligibilityEvaluateRequest } from "../InAppMessageEligibilityEvaluateRequest"
import { InAppMessageEligibilityEvaluateResult } from "../InAppMessageEligibilityEvaluateResult"
import FlowEvaluator from "../../../../flow/FlowEvaluator"
import { EvaluatorContext } from "../../../../evaluator/Evaluator"
import { DecisionReason } from "../../../../../model/model"
import { InAppMessageFrequencyCapMatcher, InAppMessageMatcher } from "../match/InAppMessageMatcher"

export type InAppMessageEligibilityFlow = EvaluationFlow<
  InAppMessageEligibilityEvaluateRequest,
  InAppMessageEligibilityEvaluateResult
>

export default interface InAppMessageEligibilityFlowEvaluator<
  R extends InAppMessageEligibilityEvaluateRequest = InAppMessageEligibilityEvaluateRequest
> extends FlowEvaluator<R, InAppMessageEligibilityEvaluateResult> {
  evaluate(
    request: R,
    context: EvaluatorContext,
    nextFlow: EvaluationFlow<R, InAppMessageEligibilityEvaluateResult>
  ): InAppMessageEligibilityEvaluateResult | undefined
}

export class PlatformInAppMessageEligibilityFlowEvaluator<
  R extends InAppMessageEligibilityEvaluateRequest = InAppMessageEligibilityEvaluateRequest
> implements InAppMessageEligibilityFlowEvaluator<R>
{
  evaluate(
    request: R,
    context: EvaluatorContext,
    nextFlow: EvaluationFlow<R, InAppMessageEligibilityEvaluateResult>
  ): InAppMessageEligibilityEvaluateResult | undefined {
    if (!request.inAppMessage.supports(request.platformType)) {
      return InAppMessageEligibilityEvaluateResult.ineligible(DecisionReason.UNSUPPORTED_PLATFORM)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class PeriodInAppMessageEligibilityFlowEvaluator<
  R extends InAppMessageEligibilityEvaluateRequest = InAppMessageEligibilityEvaluateRequest
> implements InAppMessageEligibilityFlowEvaluator<R>
{
  evaluate(
    request: R,
    context: EvaluatorContext,
    nextFlow: EvaluationFlow<R, InAppMessageEligibilityEvaluateResult>
  ): InAppMessageEligibilityEvaluateResult | undefined {
    const isWithinPeriod = request.inAppMessage.period.within(request.timestamp)
    if (!isWithinPeriod) {
      return InAppMessageEligibilityEvaluateResult.ineligible(DecisionReason.NOT_IN_IN_APP_MESSAGE_PERIOD)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class TimetableInAppMessageEligibilityFlowEvaluator<
  R extends InAppMessageEligibilityEvaluateRequest = InAppMessageEligibilityEvaluateRequest
> implements InAppMessageEligibilityFlowEvaluator<R>
{
  evaluate(
    request: R,
    context: EvaluatorContext,
    nextFlow: EvaluationFlow<R, InAppMessageEligibilityEvaluateResult>
  ): InAppMessageEligibilityEvaluateResult | undefined {
    const isWithinTimetable = request.inAppMessage.timetable.within(request.timestamp)
    if (!isWithinTimetable) {
      return InAppMessageEligibilityEvaluateResult.ineligible(DecisionReason.NOT_IN_IN_APP_MESSAGE_TIMETABLE)
    }
    return nextFlow.evaluate(request, context)
  }
}

export class HiddenInAppMessageEligibilityFlowEvaluator<
  R extends InAppMessageEligibilityEvaluateRequest = InAppMessageEligibilityEvaluateRequest
> implements InAppMessageEligibilityFlowEvaluator<R>
{
  constructor(private readonly hiddenMatcher: InAppMessageMatcher) {}

  evaluate(
    request: R,
    context: EvaluatorContext,
    nextFlow: EvaluationFlow<R, InAppMessageEligibilityEvaluateResult>
  ): InAppMessageEligibilityEvaluateResult | undefined {
    const isHidden = this.hiddenMatcher.matches(request, context)
    if (isHidden) {
      return InAppMessageEligibilityEvaluateResult.ineligible(DecisionReason.IN_APP_MESSAGE_HIDDEN)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class FrequencyInAppMessageEligibilityFlowEvaluator<
  R extends InAppMessageEligibilityEvaluateRequest = InAppMessageEligibilityEvaluateRequest
> implements InAppMessageEligibilityFlowEvaluator<R>
{
  constructor(private readonly frequencyCapMatcher: InAppMessageFrequencyCapMatcher) {}

  evaluate(
    request: R,
    context: EvaluatorContext,
    nextFlow: EvaluationFlow<R, InAppMessageEligibilityEvaluateResult>
  ): InAppMessageEligibilityEvaluateResult | undefined {
    const isFrequencyCapped = this.frequencyCapMatcher.matches(request, context)
    if (isFrequencyCapped) {
      return InAppMessageEligibilityEvaluateResult.ineligible(DecisionReason.IN_APP_MESSAGE_FREQUENCY_CAPPED)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class EligibleInAppMessageEligibilityFlowEvaluator<
  R extends InAppMessageEligibilityEvaluateRequest = InAppMessageEligibilityEvaluateRequest
> implements InAppMessageEligibilityFlowEvaluator<R>
{
  evaluate(
    request: R,
    context: EvaluatorContext,
    nextFlow: EvaluationFlow<R, InAppMessageEligibilityEvaluateResult>
  ): InAppMessageEligibilityEvaluateResult {
    return InAppMessageEligibilityEvaluateResult.eligible(DecisionReason.IN_APP_MESSAGE_TARGET)
  }
}

export {
  OverrideInAppMessageEligibilityFlowEvaluator,
  DraftInAppMessageEligibilityFlowEvaluator,
  PauseInAppMessageEligibilityFlowEvaluator,
  TargetInAppMessageEligibilityFlowEvaluator,
  LayoutResolveInAppMessageEligibilityFlowEvaluator
} from "./InAppMessageEligibilityLocalFlowEvaluator"
